import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  makeStyles,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Grid,
  CircularProgress,
  InputAdornment,
  Chip,
  Tooltip,
  Divider,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import GetAppIcon from "@material-ui/icons/GetApp";
import RefreshIcon from "@material-ui/icons/Refresh";
import ListAltIcon from "@material-ui/icons/ListAlt";
import CardGiftcardIcon from "@material-ui/icons/CardGiftcard";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import MonetizationOnIcon from "@material-ui/icons/MonetizationOn";
import FilterListIcon from "@material-ui/icons/FilterList";
import ViewListIcon from "@material-ui/icons/ViewList";
import MoveToInboxIcon from "@material-ui/icons/MoveToInbox";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import axios from "axios";
import moment from "moment-timezone";
import { URL as CLIENT_URL } from "../../config/config";

const MONEY_MICRO_UNITS = 100000;
const PAGE_SIZE = 25;
const INDIGO = "#4f46e5";
const INDIGO_SOFT = "#eef0fd";

const REASON_OPTIONS = [
  "GRANTED",
  "BACKFILL",
  "ALREADY_CLAIMED_THIS_PROJECT",
  "ALREADY_CLAIMED_THIS_FB_USER",
  "TENANT_EXCLUDED",
  "EXPIRED_REVOKED",
  "FAILED",
];
const DATE_FIELD_OPTIONS = [
  { value: "createdAt", label: "Row created" },
  { value: "fbConnectedAt", label: "FB connected" },
  { value: "adAccountSetupAt", label: "Ad account setup" },
  { value: "freeCreditGrantedAt", label: "Free credit granted" },
  { value: "firstRechargeAt", label: "First recharge" },
  { value: "spendMilestoneAt", label: "₹800 milestone" },
];

// In local dev use an interceptor-free axios instance: the global instance's
// 401 handler force-signs-out when this page's endpoints aren't reachable
// (e.g. staging backend without these routes). Production uses the global
// instance so cookie refresh keeps working.
const IS_LOCAL_DEV =
  process.env.NODE_ENV === "development" &&
  typeof window !== "undefined" &&
  window.location.hostname === "localhost";
const http = IS_LOCAL_DEV ? axios.create() : axios;

const fmtDate = (v) => (v ? moment(v).format("DD MMM YY, HH:mm") : "—");
const fmtMoney = (v, currency) => {
  if (v === null || v === undefined) return "—";
  const sym = currency === "USD" ? "$" : "₹";
  return `${sym}${(v / MONEY_MICRO_UNITS).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};
const fmtNum = (v) => (v || 0).toLocaleString("en-IN");
const fmtInr = (v) => `₹${(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#f6f7fb",
    // ProjectsLayout's route container is height:100vh + overflow:hidden —
    // pages must own their scrolling or content below the fold is clipped.
    height: "100vh",
    overflowY: "auto",
    width: "100%",
    boxSizing: "border-box",
  },
  content: { maxWidth: 1720, margin: "0 auto" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  title: { fontWeight: 700, color: "#111827", letterSpacing: -0.3 },
  subtitle: { color: "#6b7280", marginTop: 2 },
  headerBtn: {
    color: INDIGO,
    borderColor: "#e0e3ef",
    backgroundColor: "#fff",
    textTransform: "none",
    fontWeight: 600,
    "&:hover": { borderColor: INDIGO, backgroundColor: "#fbfbff" },
  },
  card: {
    borderRadius: 14,
    border: "1px solid #ebedf3",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    backgroundColor: "#fff",
  },
  sectionCard: { padding: theme.spacing(3), marginBottom: theme.spacing(2.5) },
  sectionHeading: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 17,
    fontWeight: 700,
    color: "#1f2937",
    marginBottom: theme.spacing(2.5),
  },
  // KPI
  kpiCard: {
    padding: "16px 18px",
    borderRadius: 14,
    height: "100%",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    border: "1px solid #ebedf3",
    backgroundColor: "#fff",
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  kpiIcon: {
    width: 46,
    height: 46,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  kpiLabel: { fontSize: 13, fontWeight: 600, lineHeight: 1.2 },
  kpiValue: { fontSize: 26, fontWeight: 700, lineHeight: 1.15, color: "#111827", margin: "2px 0" },
  kpiSub: { fontSize: 11.5, color: "#9aa3b2", lineHeight: 1.2 },
  // white-input override (theme forces grey pills globally)
  field: {
    "&.MuiTextField-root": {
      backgroundColor: "#fff",
      border: "1px solid #e3e7ef",
      borderRadius: 9,
      transition: "border-color .15s, box-shadow .15s",
    },
    "&.MuiTextField-root:hover": { borderColor: "#c7cede" },
    "&.MuiTextField-root:focus-within": {
      borderColor: INDIGO,
      boxShadow: `0 0 0 3px ${INDIGO}1f`,
    },
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: "#4b5563",
    display: "block",
    marginBottom: 6,
  },
  resetBtn: {
    color: INDIGO,
    borderColor: "#dcdff0",
    backgroundColor: "#fff",
    textTransform: "none",
    fontWeight: 600,
    height: 46,
    "&:hover": { borderColor: INDIGO, backgroundColor: "#fbfbff" },
  },
  chipsRow: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: theme.spacing(2.5) },
  // results
  resultsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "18px 22px",
  },
  countBadge: {
    backgroundColor: INDIGO_SOFT,
    color: INDIGO,
    borderRadius: 20,
    padding: "1px 11px",
    fontSize: 13,
    fontWeight: 700,
    marginLeft: 4,
  },
  pager: { display: "flex", alignItems: "center", gap: 10 },
  pagerBtn: {
    border: "1px solid #e3e7ef",
    borderRadius: 8,
    padding: 5,
    color: "#4b5563",
    "&:hover": { backgroundColor: "#f5f6fa" },
  },
  pagePill: {
    minWidth: 30,
    height: 30,
    borderRadius: 8,
    border: `1px solid ${INDIGO}`,
    color: INDIGO,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
  },
  tableWrap: { overflowX: "auto" },
  headCell: {
    fontWeight: 600,
    whiteSpace: "nowrap",
    backgroundColor: "#fafbfc",
    color: "#6b7280",
    fontSize: 12.5,
    borderBottom: "1px solid #edeff3",
  },
  cell: { whiteSpace: "nowrap", fontSize: 13, color: "#374151" },
  mono: { fontFamily: "monospace", fontSize: 12.5, color: "#37474f" },
  zebra: { "&:hover": { backgroundColor: "#fafbff" } },
  money: { textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
  empty: { padding: theme.spacing(9), textAlign: "center", color: "#9aa3b2" },
}));

const TRISTATE = [
  { value: "", label: "All" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

function KpiCard({ classes, icon, color, bg, value, label, sub, accentValue }) {
  return (
    <Paper className={classes.kpiCard} elevation={0}>
      <Box className={classes.kpiIcon} style={{ backgroundColor: bg, color }}>
        {icon}
      </Box>
      <Box style={{ minWidth: 0 }}>
        <Typography className={classes.kpiLabel} style={{ color }}>
          {label}
        </Typography>
        <Typography className={classes.kpiValue} style={accentValue ? { color } : undefined}>
          {value}
        </Typography>
        <Typography className={classes.kpiSub}>{sub}</Typography>
      </Box>
    </Paper>
  );
}

function StatusChip({ value }) {
  if (value === true)
    return <Chip size="small" label="Yes" style={{ backgroundColor: "#e7f6ee", color: "#1e874b" }} />;
  if (value === false)
    return <Chip size="small" label="No" style={{ backgroundColor: "#fdecea", color: "#c0392b" }} />;
  return <span>—</span>;
}

function MetaAdsJourneyPage() {
  const classes = useStyles();

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [freeCreditClaimed, setFreeCreditClaimed] = useState("");
  const [wabaConnected, setWabaConnected] = useState("");
  const [spentMilestone, setSpentMilestone] = useState("");
  const [freeCreditReason, setFreeCreditReason] = useState("");
  const [pageOrigin, setPageOrigin] = useState("");
  const [dateField, setDateField] = useState("createdAt");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const buildParams = useCallback(
    (withPaging) => {
      const params = new URLSearchParams();
      if (withPaging) {
        params.set("page", page + 1);
        params.set("limit", PAGE_SIZE);
      }
      if (search.trim()) params.set("search", search.trim());
      if (freeCreditClaimed) params.set("filter[freeCreditClaimed]", freeCreditClaimed);
      if (wabaConnected) params.set("filter[wabaConnected]", wabaConnected);
      if (spentMilestone) params.set("filter[spentMilestone]", spentMilestone);
      if (freeCreditReason) params.set("filter[freeCreditReason]", freeCreditReason);
      if (pageOrigin) params.set("filter[pageOrigin]", pageOrigin);
      if (startDate || endDate) {
        params.set("dateField", dateField);
        if (startDate) params.set("start_date", startDate);
        if (endDate) params.set("end_date", endDate);
      }
      return params;
    },
    [
      page,
      search,
      freeCreditClaimed,
      wabaConnected,
      spentMilestone,
      freeCreditReason,
      pageOrigin,
      dateField,
      startDate,
      endDate,
    ]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    // KPI stats are best-effort: a stats failure must never blank the table.
    http
      .get(`${CLIENT_URL}/superadmin/meta-ads-journey/stats?${buildParams(false).toString()}`)
      .then((r) => setStats(r.data?.stats || null))
      .catch(() => setStats(null));
    try {
      const listRes = await http.get(
        `${CLIENT_URL}/superadmin/meta-ads-journey?${buildParams(true).toString()}`
      );
      setData(listRes.data?.data || []);
      setTotal(listRes.data?.total || 0);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to load Meta Ads journey data");
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await http.get(
        `${CLIENT_URL}/superadmin/meta-ads-journey/export?${buildParams(false).toString()}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `meta-ads-journey-${moment().format("YYYY-MM-DD")}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError("CSV export failed. Please retry.");
    } finally {
      setExporting(false);
    }
  };

  const onFilterChange = (setter) => (value) => {
    setter(value);
    setPage(0);
  };

  const resetFilters = () => {
    setSearch("");
    setFreeCreditClaimed("");
    setWabaConnected("");
    setSpentMilestone("");
    setFreeCreditReason("");
    setPageOrigin("");
    setDateField("createdAt");
    setStartDate("");
    setEndDate("");
    setPage(0);
  };

  const yn = (v) => (v === "true" ? "Yes" : "No");
  const activeChips = [];
  if (search.trim())
    activeChips.push({ label: `Search: "${search.trim()}"`, clear: () => onFilterChange(setSearch)("") });
  if (freeCreditClaimed)
    activeChips.push({ label: `Credit claimed: ${yn(freeCreditClaimed)}`, clear: () => onFilterChange(setFreeCreditClaimed)("") });
  if (wabaConnected)
    activeChips.push({ label: `WABA: ${yn(wabaConnected)}`, clear: () => onFilterChange(setWabaConnected)("") });
  if (spentMilestone)
    activeChips.push({ label: `Spent ₹800: ${yn(spentMilestone)}`, clear: () => onFilterChange(setSpentMilestone)("") });
  if (freeCreditReason)
    activeChips.push({ label: `Reason: ${freeCreditReason}`, clear: () => onFilterChange(setFreeCreditReason)("") });
  if (pageOrigin)
    activeChips.push({ label: `Page: ${pageOrigin}`, clear: () => onFilterChange(setPageOrigin)("") });
  if (startDate || endDate) {
    const fld = DATE_FIELD_OPTIONS.find((o) => o.value === dateField)?.label || dateField;
    activeChips.push({
      label: `${fld}: ${startDate || "…"} → ${endDate || "…"}`,
      clear: () => {
        setStartDate("");
        setEndDate("");
        setPage(0);
      },
    });
  }

  const selectField = (value, setter, options) => (
    <TextField
      fullWidth
      size="small"
      variant="outlined"
      select
      className={classes.field}
      value={value}
      onChange={(e) => onFilterChange(setter)(e.target.value)}
      SelectProps={{ displayEmpty: true }}
    >
      {options.map((o) => (
        <MenuItem key={o.label} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
  const triSelect = (value, setter) => selectField(value, setter, TRISTATE);

  const KPIS = stats
    ? [
        { icon: <ListAltIcon />, color: INDIGO, bg: "#eef0fd", value: fmtNum(stats.total), label: "Total projects", sub: "All projects created" },
        { icon: <CardGiftcardIcon />, color: "#1e874b", bg: "#e7f6ee", value: fmtNum(stats.claimed), label: "Free credit claimed", sub: "Projects with free credit" },
        { icon: <TrendingUpIcon />, color: "#c07a12", bg: "#fdf1dd", value: fmtNum(stats.spentMilestone), label: "Spent ₹800+", sub: "Projects above ₹800" },
        { icon: <WhatsAppIcon />, color: "#0f9d76", bg: "#e2f5ee", value: fmtNum(stats.wabaConnected), label: "WABA connected", sub: "Connected accounts" },
        { icon: <AccountBalanceWalletIcon />, color: "#6d28d9", bg: "#f0eafc", value: fmtInr(stats.totalRecharged), label: "Total recharged", sub: "Total amount recharged", accentValue: true },
        { icon: <MonetizationOnIcon />, color: "#dc2626", bg: "#fdeaea", value: fmtInr(stats.totalSpent), label: "Total spent", sub: "Total amount spent", accentValue: true },
      ]
    : [
        { icon: <ListAltIcon />, color: INDIGO, bg: "#eef0fd", value: "—", label: "Total projects", sub: "All projects created" },
        { icon: <CardGiftcardIcon />, color: "#1e874b", bg: "#e7f6ee", value: "—", label: "Free credit claimed", sub: "Projects with free credit" },
        { icon: <TrendingUpIcon />, color: "#c07a12", bg: "#fdf1dd", value: "—", label: "Spent ₹800+", sub: "Projects above ₹800" },
        { icon: <WhatsAppIcon />, color: "#0f9d76", bg: "#e2f5ee", value: "—", label: "WABA connected", sub: "Connected accounts" },
        { icon: <AccountBalanceWalletIcon />, color: "#6d28d9", bg: "#f0eafc", value: "—", label: "Total recharged", sub: "Total amount recharged" },
        { icon: <MonetizationOnIcon />, color: "#dc2626", bg: "#fdeaea", value: "—", label: "Total spent", sub: "Total amount spent" },
      ];

  const COLUMNS = [
    "Project ID",
    "Client ID",
    "FB Connected",
    "WABA",
    "Page Origin",
    "Ad Account",
    "Ad Account Setup",
    "1st Ad Status",
    "Ads Live",
    "Recharged",
    "Spent",
    "₹800",
    "Credit Claimed",
    "Credit Reason",
    "Credit Granted",
  ];

  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min(total, (page + 1) * PAGE_SIZE);
  const canPrev = page > 0;
  const canNext = (page + 1) * PAGE_SIZE < total;

  return (
    <Box className={classes.root}>
      <Box className={classes.content}>
        {/* Header */}
        <Box className={classes.header}>
          <Box>
            <Typography variant="h5" className={classes.title}>
              Meta Ads Project Journey
            </Typography>
            <Typography variant="body2" className={classes.subtitle}>
              One row per project setup — the full growth funnel and free-credit status.
            </Typography>
          </Box>
          <Box style={{ display: "flex", gap: 10 }}>
            <Button
              variant="outlined"
              className={classes.headerBtn}
              startIcon={<RefreshIcon />}
              onClick={fetchData}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              className={classes.headerBtn}
              startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <GetAppIcon />}
              onClick={handleExport}
              disabled={exporting || total === 0}
            >
              Download CSV
            </Button>
          </Box>
        </Box>

        {/* Summary */}
        <Grid container spacing={2} style={{ marginBottom: 40 }}>
          {KPIS.map((kpi) => (
            <Grid item xs={6} sm={4} md={2} key={kpi.label}>
              <KpiCard classes={classes} {...kpi} />
            </Grid>
          ))}
        </Grid>

        {/* Filters */}
        <Paper className={`${classes.card} ${classes.sectionCard}`} elevation={0}>
          <Typography component="div" className={classes.sectionHeading}>
            <FilterListIcon style={{ color: INDIGO }} /> Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <label className={classes.fieldLabel}>Search</label>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                className={classes.field}
                placeholder="Project / client / FB user / ad account"
                value={search}
                onChange={(e) => onFilterChange(setSearch)(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" style={{ color: "#98a2b3" }} />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => onFilterChange(setSearch)("")}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <label className={classes.fieldLabel}>Credit claimed</label>
              {triSelect(freeCreditClaimed, setFreeCreditClaimed)}
            </Grid>
            <Grid item xs={6} md={2}>
              <label className={classes.fieldLabel}>WABA</label>
              {triSelect(wabaConnected, setWabaConnected)}
            </Grid>
            <Grid item xs={6} md={2}>
              <label className={classes.fieldLabel}>Spent ₹800</label>
              {triSelect(spentMilestone, setSpentMilestone)}
            </Grid>
            <Grid item xs={6} md={2}>
              <label className={classes.fieldLabel}>Page origin</label>
              {selectField(pageOrigin, setPageOrigin, [
                { value: "", label: "All" },
                { value: "CREATED", label: "Created (new)" },
                { value: "ATTACHED", label: "Attached (old)" },
              ])}
            </Grid>

            <Grid item xs={12} md={4}>
              <label className={classes.fieldLabel}>Credit reason</label>
              {selectField(freeCreditReason, setFreeCreditReason, [
                { value: "", label: "All" },
                ...REASON_OPTIONS.map((r) => ({ value: r, label: r })),
              ])}
            </Grid>
            <Grid item xs={6} md={2}>
              <label className={classes.fieldLabel}>Date field</label>
              {selectField(
                dateField,
                setDateField,
                DATE_FIELD_OPTIONS.map((o) => ({ value: o.value, label: o.label }))
              )}
            </Grid>
            <Grid item xs={6} md={2}>
              <label className={classes.fieldLabel}>From</label>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                type="date"
                className={classes.field}
                value={startDate}
                onChange={(e) => onFilterChange(setStartDate)(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <label className={classes.fieldLabel}>To</label>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                type="date"
                className={classes.field}
                value={endDate}
                onChange={(e) => onFilterChange(setEndDate)(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} md={2} style={{ display: "flex", alignItems: "flex-end" }}>
              <Button
                fullWidth
                variant="outlined"
                className={classes.resetBtn}
                startIcon={<CloseIcon fontSize="small" />}
                onClick={resetFilters}
              >
                Reset filters
              </Button>
            </Grid>
          </Grid>

          {activeChips.length > 0 && (
            <>
              <Divider style={{ marginTop: 20 }} />
              <Box className={classes.chipsRow}>
                <Typography variant="caption" style={{ color: "#6b7280", alignSelf: "center" }}>
                  Active:
                </Typography>
                {activeChips.map((c) => (
                  <Chip key={c.label} size="small" label={c.label} onDelete={c.clear} />
                ))}
              </Box>
            </>
          )}
        </Paper>

        {/* Results */}
        <Paper className={classes.card} elevation={0}>
          <Box className={classes.resultsHeader}>
            <Typography component="div" className={classes.sectionHeading} style={{ marginBottom: 0 }}>
              <ViewListIcon style={{ color: INDIGO }} /> Results
              <span className={classes.countBadge}>{fmtNum(total)}</span>
            </Typography>
            <Box className={classes.pager}>
              <Typography variant="body2" style={{ color: "#6b7280" }}>
                Showing {from}-{to} of {fmtNum(total)}
              </Typography>
              <IconButton
                className={classes.pagerBtn}
                size="small"
                disabled={!canPrev}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                <NavigateBeforeIcon fontSize="small" />
              </IconButton>
              <span className={classes.pagePill}>{page + 1}</span>
              <IconButton
                className={classes.pagerBtn}
                size="small"
                disabled={!canNext}
                onClick={() => setPage((p) => p + 1)}
              >
                <NavigateNextIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {error ? (
            <Typography style={{ color: "#c0392b", padding: "0 22px 12px" }}>{error}</Typography>
          ) : null}

          <TableContainer className={classes.tableWrap}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {COLUMNS.map((h) => (
                    <TableCell
                      key={h}
                      className={classes.headCell}
                      align={["Recharged", "Spent", "Ads Live"].includes(h) ? "right" : "left"}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length} className={classes.empty}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length} className={classes.empty}>
                      <MoveToInboxIcon style={{ fontSize: 52, color: "#cdd3de" }} />
                      <Typography style={{ marginTop: 8, fontWeight: 600, color: "#6b7280" }}>
                        No records found
                      </Typography>
                      <Typography variant="caption">
                        Try clearing filters. Rows appear as projects move through ad setup.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((r) => (
                    <TableRow key={r._id} hover className={classes.zebra}>
                      <TableCell className={`${classes.cell} ${classes.mono}`}>{r.assistantId || "—"}</TableCell>
                      <TableCell className={`${classes.cell} ${classes.mono}`}>{r.clientId || "—"}</TableCell>
                      <TableCell className={classes.cell}>{fmtDate(r.fbConnectedAt)}</TableCell>
                      <TableCell className={classes.cell}>
                        <StatusChip value={r.wabaConnected} />
                      </TableCell>
                      <TableCell className={classes.cell}>
                        {r.pageOrigin ? (
                          <Chip size="small" variant="outlined" label={r.pageOrigin === "CREATED" ? "New" : "Old"} />
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className={`${classes.cell} ${classes.mono}`}>{r.adAccountId || "—"}</TableCell>
                      <TableCell className={classes.cell}>{fmtDate(r.adAccountSetupAt)}</TableCell>
                      <TableCell className={classes.cell}>
                        {r.firstAdStatus ? (
                          <Chip size="small" variant="outlined" label={r.firstAdStatus} />
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className={classes.money}>{r.totalAdsLive ?? 0}</TableCell>
                      <TableCell className={classes.money}>{fmtMoney(r.totalRecharged, r.currency)}</TableCell>
                      <TableCell className={classes.money}>{fmtMoney(r.cumulativeCreditSpent, r.currency)}</TableCell>
                      <TableCell className={classes.cell}>
                        <StatusChip value={r.spentMilestone} />
                      </TableCell>
                      <TableCell className={classes.cell}>
                        <StatusChip value={r.freeCreditClaimed} />
                      </TableCell>
                      <TableCell className={classes.cell}>
                        {r.freeCreditReason ? (
                          <Tooltip title={r.freeCreditReason}>
                            <Chip
                              size="small"
                              label={r.freeCreditReason.replace(/_/g, " ").toLowerCase()}
                              style={{
                                backgroundColor:
                                  r.freeCreditReason === "GRANTED" || r.freeCreditReason === "BACKFILL"
                                    ? "#e7f6ee"
                                    : r.freeCreditReason === "FAILED"
                                    ? "#fdecea"
                                    : "#eef1f5",
                                color:
                                  r.freeCreditReason === "GRANTED" || r.freeCreditReason === "BACKFILL"
                                    ? "#1e874b"
                                    : r.freeCreditReason === "FAILED"
                                    ? "#c0392b"
                                    : "#4b5563",
                              }}
                            />
                          </Tooltip>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className={classes.cell}>{fmtDate(r.freeCreditGrantedAt)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Box>
  );
}

export default MetaAdsJourneyPage;
